import { memo, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { processText } from '../../../../utils/learnUtils';
import { useMathJax } from '../../../../hooks/useMathJax';

const THUMB_D = 22; // px — thumb diameter at reference width (215px)

/* Pulsing opacity animation for the thumb glow ring */
function usePulseOpacity() {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - start) / 1000;
      setOpacity((Math.sin(2 * Math.PI * 0.375 * t) + 1) / 8);
    }, 50);
    return () => clearInterval(id);
  }, []);
  return opacity;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Slider control for an interactive figure parameter.
 * @param {{ value, min, max, name, color, onChange }} props
 */
function IntFigSlider({ value, min, max, name, color = '#1f76D2', onChange }) {
  const trackRef = useRef(null);
  const labelRef = useRef(null);
  const thumbRef = useRef(null);
  const [trackW, setTrackW] = useState(0);
  const [showValue, setShowValue] = useState(false);
  const pulseOpacity = usePulseOpacity();

  /* Coerce potentially malformed/out-of-range props (e.g. a slider whose
     "param index" field points past the params array) to finite numbers so
     rendering never throws — worst case the slider just sits at its min. */
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : safeMin + 1;
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : safeMin;

  /* Measure track width */
  useEffect(() => {
    if (!trackRef.current) return;
    const ro = new ResizeObserver(([entry]) => setTrackW(entry.contentRect.width));
    ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  /* MathJax for label */
  const labelHtml = processText(name ?? '');
  useMathJax(labelRef, [name]);

  const thumbD  = THUMB_D * Math.max(trackW, 10) / 215;
  const travel  = Math.max(trackW - thumbD, 1);
  const range   = safeMax - safeMin || 1;
  const thumbX  = ((safeValue - safeMin) / range) * travel;
  const fillPct = ((safeValue - safeMin) / range) * 100;

  const handleDrag = (_, pos) => {
    const v = (pos.x / travel) * range + safeMin;
    onChange(Math.max(safeMin, Math.min(safeMax, v)));
  };

  return (
    <div className="ct__intfig-slider-wrap">
      {/* Tooltip value */}
      <div className="ct__intfig-slider-value-row" style={{ height: thumbD }}>
        {showValue && (
          <div
            className="ct__intfig-slider-value"
            style={{
              left: thumbX + thumbD / 2,
              transform: 'translateX(-50%)',
              fontSize: Math.max(9, 11 * Math.max(trackW, 10) / 215),
            }}
          >
            {safeValue.toFixed(2)}
          </div>
        )}
      </div>

      {/* Track */}
      <div ref={trackRef} className="ct__intfig-slider-track" style={{ height: thumbD }}>
        {trackW > 0 && (
          <>
            {/* Rail */}
            <svg
              width={travel}
              height={thumbD}
              style={{ position: 'absolute', left: thumbD / 2, top: 0 }}
              preserveAspectRatio="none"
            >
              <line x1={0} y1={thumbD / 2} x2={travel} y2={thumbD / 2}
                stroke={color + '66'} strokeWidth="3" strokeLinecap="round" />
              <line x1={0} y1={thumbD / 2} x2={fillPct / 100 * travel} y2={thumbD / 2}
                stroke={color} strokeWidth="3" strokeLinecap="round" />
            </svg>

            {/* Draggable thumb */}
            <div className="ct__intfig-slider-thumb" style={{ position: 'absolute', top: 0, left: 0 }}>
              <Draggable
                nodeRef={thumbRef}
                axis="x"
                position={{ x: thumbX, y: 0 }}
                bounds={{ left: 0, right: travel, top: 0, bottom: 0 }}
                onDrag={handleDrag}
              >
                <div
                  ref={thumbRef}
                  style={{ width: thumbD, height: thumbD, position: 'absolute' }}
                  onMouseEnter={() => setShowValue(true)}
                  onMouseLeave={() => setShowValue(false)}
                >
                  <svg width="100%" height="100%" viewBox="-0.5 -0.5 1 1">
                    <circle cx={0} cy={0} r={0.5} fill={hexToRgba(color, pulseOpacity)} />
                    <circle cx={0} cy={0} r={0.25} fill={color} />
                  </svg>
                </div>
              </Draggable>
            </div>
          </>
        )}
      </div>

      {/* Label */}
      <div
        ref={labelRef}
        className="ct__intfig-slider-label"
        style={{ fontSize: Math.max(13, 13 * Math.max(trackW, 10) / 215) }}
        dangerouslySetInnerHTML={{ __html: labelHtml }}
      />
    </div>
  );
}

export default memo(IntFigSlider);
